import { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';

// Layouts
import AdminLayout from './layouts/AdminLayout';

// Pages
import Setup from './pages/Setup';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Companies from './pages/Companies';
import Regions from './pages/Regions';
import Issues from './pages/Issues';
import AIProjectManager from './pages/AIProjectManager';
import WarehouseStorage from './pages/WarehouseStorage';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import Documentation from './pages/Documentation';
import SubscriptionPlans from './pages/SubscriptionPlans';
import Invoices from './pages/Invoices';
import EnterpriseInquiries from './pages/EnterpriseInquiries';
import Mailbox from './pages/Mailbox';
import SystemSettings from './pages/SystemSettings';
import Referrals from './pages/Referrals';
import InviteCodes from './pages/InviteCodes';
import CacheManagement from './pages/CacheManagement';

// Loading spinner
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-surface-900 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-surface-400">Loading...</p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, setupRequired } = useAuthStore();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (setupRequired) {
    return <Navigate to="/setup" replace />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Public route wrapper (redirects if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading, setupRequired } = useAuthStore();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (setupRequired) {
    return <Navigate to="/setup" replace />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Setup route wrapper
function SetupRoute({ children }) {
  const { isLoading, setupRequired, isAuthenticated } = useAuthStore();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (!setupRequired) {
    if (isAuthenticated) {
      return <Navigate to="/" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function App() {
  const initialize = useAuthStore((state) => state.initialize);
  
  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
  
  return (
    <HashRouter>
      <Toaster 
        position="top-right"
        toastOptions={{
          className: 'bg-surface-800 text-surface-100 border border-surface-700',
          duration: 4000,
          style: {
            background: '#1e293b',
            color: '#f1f5f9',
            border: '1px solid #334155'
          }
        }}
      />
      
      <Routes>
        {/* Setup route */}
        <Route 
          path="/setup" 
          element={
            <SetupRoute>
              <Setup />
            </SetupRoute>
          } 
        />
        
        {/* Login route */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        
        {/* Protected admin routes */}
        <Route 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="/users" element={<Users />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/regions" element={<Regions />} />
          <Route path="/warehouse" element={<WarehouseStorage />} />
          <Route path="/issues" element={<Issues />} />
          <Route path="/ai-pm" element={<AIProjectManager />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/documentation" element={<Documentation />} />
          <Route path="/subscription-plans" element={<SubscriptionPlans />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/enterprise-inquiries" element={<EnterpriseInquiries />} />
          <Route path="/mailbox" element={<Mailbox />} />
          <Route path="/mailbox/:mailboxType" element={<Mailbox />} />
          <Route path="/referrals" element={<Referrals />} />
          <Route path="/invite-codes" element={<InviteCodes />} />
          <Route path="/cache" element={<CacheManagement />} />
          <Route path="/settings" element={<SystemSettings />} />
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </HashRouter>
  );
}

export default App;

